
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { addDays, addMonths, format, isAfter, isBefore, parseISO } from "date-fns";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const formSchema = z.object({
  name: z.string().min(1, "Academic year name is required"),
  autoCreateTerms: z.boolean().default(false),
  yearStart: z.string().optional(),
  term1Start: z.string().min(1, "Term 1 start date is required"),
  term1End: z.string().min(1, "Term 1 end date is required"),
  term2Start: z.string().min(1, "Term 2 start date is required"),
  term2End: z.string().min(1, "Term 2 end date is required"),
  term3Start: z.string().min(1, "Term 3 start date is required"),
  term3End: z.string().min(1, "Term 3 end date is required"),
}).refine((data) => {
  const term1Start = parseISO(data.term1Start);
  const term1End = parseISO(data.term1End);
  const term2Start = parseISO(data.term2Start);
  const term2End = parseISO(data.term2End);
  const term3Start = parseISO(data.term3Start);
  const term3End = parseISO(data.term3End);

  if (!isAfter(term1End, term1Start)) return false;
  if (!isAfter(term2End, term2Start)) return false;
  if (!isAfter(term3End, term3Start)) return false;

  if (!isBefore(term1End, term2Start)) return false;
  if (!isBefore(term2End, term3Start)) return false;

  return true;
}, {
  message: "Terms cannot overlap. Make sure each term ends before the next one starts with at least one day gap."
});

type FormValues = z.infer<typeof formSchema>;

type AcademicYearFormProps = {
  onSuccess: () => void;
  onClose: () => void;
};

export function AcademicYearForm({ onSuccess, onClose }: AcademicYearFormProps) {
  const { toast } = useToast();
  const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [successDialogOpen, setSuccessDialogOpen] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState("");
  
  const defaultValues: FormValues = {
    name: "",
    autoCreateTerms: false,
    yearStart: "",
    term1Start: "",
    term1End: "",
    term2Start: "",
    term2End: "",
    term3Start: "",
    term3End: "",
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const autoCreateTerms = form.watch("autoCreateTerms");
  const yearStart = form.watch("yearStart");

  React.useEffect(() => {
    if (autoCreateTerms && yearStart) {
      updateTermDates(yearStart);
    }
  }, [autoCreateTerms, yearStart]);

  const updateTermDates = (startDate: string) => {
    if (!startDate) return;

    const start = new Date(startDate);
    
    const term1End = addMonths(start, 4);
    const term2Start = addDays(term1End, 1);
    const term2End = addMonths(term2Start, 4);
    const term3Start = addDays(term2End, 1);
    const term3End = addMonths(term3Start, 4);

    form.setValue("term1Start", format(start, "yyyy-MM-dd"));
    form.setValue("term1End", format(term1End, "yyyy-MM-dd"));
    form.setValue("term2Start", format(term2Start, "yyyy-MM-dd"));
    form.setValue("term2End", format(term2End, "yyyy-MM-dd"));
    form.setValue("term3Start", format(term3Start, "yyyy-MM-dd"));
    form.setValue("term3End", format(term3End, "yyyy-MM-dd"));
  };

  const handleSuccessClose = () => {
    setSuccessDialogOpen(false);
    onClose();
    form.reset();
    onSuccess();
  };

  const onSubmit = async (values: FormValues) => {
    try {
      // First check if an academic year with this name already exists
      const { data: existingYear } = await supabase
        .from("academic_years")
        .select("id")
        .eq("name", values.name)
        .maybeSingle();

      if (existingYear) {
        setErrorMessage(`An academic year named "${values.name}" already exists. Please choose a different name.`);
        setErrorDialogOpen(true);
        return;
      }

      const { data: year, error: yearError } = await supabase
        .from("academic_years")
        .insert({ name: values.name })
        .select()
        .single();

      if (yearError || !year) {
        console.error("Error creating academic year:", yearError);
        setErrorMessage("Failed to create academic year. Please try again.");
        setErrorDialogOpen(true);
        return;
      }

      const terms = [
        {
          name: "Term 1",
          academic_year_id: year.id,
          start_date: values.term1Start,
          end_date: values.term1End,
        },
        {
          name: "Term 2",
          academic_year_id: year.id,
          start_date: values.term2Start,
          end_date: values.term2End,
        },
        {
          name: "Term 3",
          academic_year_id: year.id,
          start_date: values.term3Start,
          end_date: values.term3End,
        },
      ];

      const { error: termsError } = await supabase
        .from("terms")
        .insert(terms);

      if (termsError) {
        // If terms creation fails, clean up by deleting the academic year
        await supabase
          .from("academic_years")
          .delete()
          .eq("id", year.id);

        console.error("Error creating terms:", termsError);
        setErrorMessage("Failed to create terms. Please ensure there is no overlap between terms and each term has at least one day gap.");
        setErrorDialogOpen(true);
        return;
      }

      setSuccessMessage(`Academic year "${values.name}" created successfully with all terms.`);
      setSuccessDialogOpen(true);
    } catch (error: any) {
      console.error("Error saving academic year:", error);
      setErrorMessage("An unexpected error occurred. Please try again.");
      setErrorDialogOpen(true);
    }
  };

  return (
    <>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create New Academic Year</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Academic Year Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., 2024-2025" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="autoCreateTerms"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    Automatically create terms
                  </FormLabel>
                  <FormDescription>
                    Each term will be 4 months long
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          {autoCreateTerms && (
            <>
              <FormField
                control={form.control}
                name="yearStart"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Year Start Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="term1Start"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Term 1 Start</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="term1End"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Term 1 End</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="term2Start"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Term 2 Start</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="term2End"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Term 2 End</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="term3Start"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Term 3 Start</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="term3End"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Term 3 End</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </>
          )}

          {!autoCreateTerms && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="term1Start"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Term 1 Start</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="term1End"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Term 1 End</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="term2Start"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Term 2 Start</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="term2End"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Term 2 End</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="term3Start"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Term 3 Start</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="term3End"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Term 3 End</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </>
          )}

            <Button type="submit" className="w-full">
              Create Academic Year
            </Button>
          </form>
        </Form>
      </DialogContent>

      <Dialog open={errorDialogOpen} onOpenChange={setErrorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription className="text-destructive">
              {errorMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setErrorDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Success</DialogTitle>
            <DialogDescription className="text-green-600">
              {successMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={handleSuccessClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
